import { lazy, ComponentType, LazyExoticComponent } from "react";

// Type for lazy component with preload method
export type LazyComponentWithPreload<T extends ComponentType<any>> =
  LazyExoticComponent<T> & {
    preload: () => Promise<{ default: T }>;
  };

// Enhanced lazy loading with preloading capability
export function lazyWithPreload<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
): LazyComponentWithPreload<T> {
  const Component = lazy(importFunc) as LazyComponentWithPreload<T>;

  // Add preload method to the component
  Component.preload = importFunc;

  return Component;
}

// Preload multiple components
export function preloadComponents(
  components: Array<{ preload: () => Promise<any> }>,
) {
  return Promise.all(components.map((component) => component.preload()));
}

// Route-based preloading with component registry
let componentRegistry: Record<
  string,
  Array<{ preload: () => Promise<any> }>
> = {};

// Register components for a specific route
export function registerRouteComponents(
  route: string,
  components: Array<{ preload: () => Promise<any> }>,
) {
  componentRegistry[route] = components;
}

// Route-based preloading
export function preloadRouteComponents(route: string) {
  return componentRegistry[route]
    ? preloadComponents(componentRegistry[route])
    : Promise.resolve();
}

// Clear component registry (useful for testing)
export function clearComponentRegistry() {
  componentRegistry = {};
}
